(() => {
  'use strict';

  const STYLE_ID = 'swan-native-account-style-v9';
  const ROOTS_OBSERVED = new WeakSet();
  const ACCOUNTS_BOUND = new WeakSet();
  let scanFrame = 0;
  let scanTimer = 0;
  let accountInteractionUntil = 0;

  const ICONS = {
    orders: `
      <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
        <rect x="7" y="4.75" width="18" height="22.5" rx="4.5"></rect>
        <path d="M11 10h10M11 14.7h10M11 19.4h5.8"></path>
        <circle class="swan-native-account-icon__badge" cx="23.2" cy="23.1" r="5.1"></circle>
        <path class="swan-native-account-icon__badge-check" d="m20.8 23.1 1.55 1.55 3.2-3.45"></path>
      </svg>`,
    profile: `
      <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
        <rect x="4.75" y="7" width="22.5" height="18" rx="4.25"></rect>
        <circle cx="12" cy="13" r="3"></circle>
        <path d="M7.75 21c.62-3.02 2.04-4.52 4.25-4.52s3.63 1.5 4.25 4.52M19 12h4.2M19 16h4.2M19 20h2.8"></path>
        <path class="swan-native-account-icon__accent" d="M12 4.35V7"></path>
      </svg>`,
    favourites: `
      <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
        <path d="M16 27 6.7 18.25A6.9 6.9 0 0 1 16 8.2a6.9 6.9 0 0 1 9.3 10.05L16 27Z"></path>
        <path class="swan-native-account-icon__accent" d="M24.8 4.4v4.2M22.7 6.5h4.2"></path>
      </svg>`
  };

  const normalize = (value = '') => value
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  const capturedRoot = (host) => {
    if (!host) return null;
    return host.shadowRoot || window.__swanShadowRoots?.get(host) || null;
  };

  const rootBelongsToAccount = (root) => {
    let current = root;

    while (current && current.host) {
      const host = current.host;
      if (host.matches?.('shopify-account')) return true;

      const parentRoot = host.getRootNode?.();
      current = parentRoot && parentRoot.host ? parentRoot : null;
    }

    return false;
  };

  const classify = (element) => {
    if (!(element instanceof Element)) return '';
    if (element.closest?.('.swan-customer-account-menu, [data-swan-native-account-link]')) return '';

    const text = normalize(element.textContent);
    const aria = normalize(element.getAttribute('aria-label') || '');
    const href = normalize(element.getAttribute('href') || '');
    const combined = `${text} ${aria}`.trim();

    if (combined.length > 90) return '';

    if (/^(my )?(favourites|favorites|wishlist|saved items|saved inspiration|mes favoris)(\b|$)/.test(combined)
      || /(favourite|favorite|wishlist|favoris)/.test(href)) return 'favourites';

    if (/^(profile|account profile|account details|your details|profil)(\b|$)/.test(combined)
      || /\/profile(?:[/?#]|$)/.test(href)) return 'profile';

    if (/^(orders?|order history|my orders?|commandes?)(\b|$)/.test(combined)
      || /\/orders?(?:[/?#]|$)/.test(href)) return 'orders';

    return '';
  };

  const interactiveElements = (scope) => Array.from(
    scope.querySelectorAll?.('a, button, [role="link"], [role="menuitem"]') || []
  );

  const elementScore = (element) => {
    const textLength = normalize(element.textContent).length;
    const descendantLinks = element.querySelectorAll?.('a, button, [role="link"], [role="menuitem"]').length || 0;
    return textLength + (descendantLinks * 50);
  };

  const selectMatches = (scope) => {
    const grouped = { orders: [], profile: [], favourites: [] };

    interactiveElements(scope).forEach((element) => {
      const key = classify(element);
      if (key) grouped[key].push(element);
    });

    const matches = {};
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => elementScore(a) - elementScore(b));
      matches[key] = grouped[key][0] || null;
    });

    return matches;
  };

  const commonAncestor = (elements, boundary) => {
    let candidate = elements[0]?.parentElement || null;

    while (candidate) {
      if (elements.every((element) => candidate.contains(element))) return candidate;
      if (candidate === boundary) break;
      candidate = candidate.parentElement;
    }

    return null;
  };

  const directBranch = (container, element) => {
    let branch = element;
    while (branch.parentElement && branch.parentElement !== container) branch = branch.parentElement;
    return branch;
  };

  const injectStyles = (root) => {
    const styleHost = root === document ? document.head : root;
    if (!styleHost?.querySelector || styleHost.querySelector(`#${STYLE_ID}`)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      [data-swan-native-account-shortcuts] {
        position: relative !important;
        display: grid !important;
        grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
        gap: 0 !important;
        width: 100% !important;
        margin: 0 !important;
        padding: 7px !important;
        overflow: hidden !important;
        border: 1px solid rgba(105, 16, 41, .13) !important;
        border-radius: 18px !important;
        background: #ffffff !important;
        box-shadow: 0 10px 28px rgba(55, 24, 35, .08) !important;
        box-sizing: border-box !important;
      }

      [data-swan-native-account-shortcuts]::before {
        position: absolute !important;
        top: 0 !important;
        right: 0 !important;
        left: 0 !important;
        height: 2px !important;
        background: linear-gradient(90deg, #691029 0 72%, #c9994c 72% 100%) !important;
        content: '' !important;
      }

      [data-swan-native-account-branch] {
        display: block !important;
        min-width: 0 !important;
        height: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        border: 0 !important;
        background: transparent !important;
        list-style: none !important;
        box-shadow: none !important;
      }

      [data-swan-native-account-branch="profile"],
      [data-swan-native-account-branch="favourites"] {
        border-left: 1px solid rgba(105, 16, 41, .1) !important;
      }

      [data-swan-native-account-link] {
        position: relative !important;
        display: flex !important;
        min-width: 0 !important;
        min-height: 82px !important;
        height: 100% !important;
        align-items: center !important;
        justify-content: center !important;
        flex-direction: column !important;
        gap: 7px !important;
        margin: 0 !important;
        padding: 10px 6px 8px !important;
        overflow: hidden !important;
        border: 0 !important;
        border-radius: 12px !important;
        background: transparent !important;
        color: #691029 !important;
        font-family: var(--shopify-account-font-body, inherit) !important;
        font-size: 12px !important;
        font-weight: 760 !important;
        letter-spacing: -.01em !important;
        line-height: 1.18 !important;
        text-align: center !important;
        text-decoration: none !important;
        box-shadow: none !important;
        box-sizing: border-box !important;
        transition: background-color 160ms ease, color 160ms ease, transform 160ms ease !important;
      }

      [data-swan-native-account-link]::after {
        position: absolute !important;
        right: 28% !important;
        bottom: 5px !important;
        left: 28% !important;
        height: 2px !important;
        border-radius: 999px !important;
        background: #c9994c !important;
        content: '' !important;
        opacity: 0 !important;
        transform: scaleX(.55) !important;
        transition: opacity 160ms ease, transform 160ms ease !important;
      }

      [data-swan-native-account-link] > :not(.swan-native-account-icon),
      [data-swan-native-account-link] > :not(.swan-native-account-icon) * {
        color: inherit !important;
        font-family: inherit !important;
        font-size: inherit !important;
        font-weight: inherit !important;
        line-height: inherit !important;
        text-align: center !important;
      }

      [data-swan-native-account-link][data-swan-native-account-layout="row"] {
        min-height: 56px !important;
        height: auto !important;
        align-items: center !important;
        justify-content: flex-start !important;
        flex-direction: row !important;
        gap: 11px !important;
        margin: 4px 0 !important;
        padding: 7px 10px !important;
        border: 1px solid rgba(105, 16, 41, .1) !important;
        border-radius: 13px !important;
        background: #ffffff !important;
        text-align: left !important;
      }

      [data-swan-native-account-link][data-swan-native-account-layout="row"]::after {
        top: 14px !important;
        right: auto !important;
        bottom: 14px !important;
        left: 0 !important;
        width: 2px !important;
        height: auto !important;
        border-radius: 0 999px 999px 0 !important;
        opacity: 1 !important;
        transform: none !important;
      }

      [data-swan-native-account-link][data-swan-native-account-layout="row"] > :not(.swan-native-account-icon),
      [data-swan-native-account-link][data-swan-native-account-layout="row"] > :not(.swan-native-account-icon) * {
        text-align: left !important;
      }

      [data-swan-native-account-link][data-swan-native-account-layout="row"] .swan-native-account-icon {
        width: 36px !important;
        height: 36px !important;
        min-width: 36px !important;
        flex-basis: 36px !important;
        border-radius: 10px !important;
      }

      [data-swan-native-account-link][data-swan-native-account-layout="row"] .swan-native-account-icon svg {
        width: 22px !important;
        height: 22px !important;
      }

      [data-swan-native-account-link]:hover,
      [data-swan-native-account-link]:focus-visible {
        background: #faf5f6 !important;
        color: #5a0b22 !important;
        outline: none !important;
        transform: translateY(-1px) !important;
      }

      [data-swan-native-account-link]:hover::after,
      [data-swan-native-account-link]:focus-visible::after {
        opacity: 1 !important;
        transform: scaleX(1) !important;
      }

      [data-swan-native-account-link] > svg:not(.swan-native-account-icon),
      [data-swan-native-account-link] > [aria-hidden="true"]:not(.swan-native-account-icon) {
        display: none !important;
      }

      .swan-native-account-icon {
        position: relative !important;
        display: grid !important;
        width: 39px !important;
        height: 39px !important;
        min-width: 39px !important;
        flex: 0 0 39px !important;
        place-items: center !important;
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
        border: 1px solid rgba(105, 16, 41, .14) !important;
        border-radius: 11px !important;
        background: linear-gradient(145deg, #fff, #f8eff2) !important;
        color: #691029 !important;
        box-shadow: inset 0 -2px 0 rgba(201, 153, 76, .55) !important;
        box-sizing: border-box !important;
      }

      .swan-native-account-icon svg {
        display: block !important;
        width: 24px !important;
        height: 24px !important;
        overflow: visible !important;
        fill: none !important;
        stroke: currentColor !important;
        stroke-width: 1.45 !important;
        stroke-linecap: round !important;
        stroke-linejoin: round !important;
      }

      .swan-native-account-icon__badge {
        fill: #c9994c !important;
        stroke: #ffffff !important;
        stroke-width: 1.05 !important;
      }

      .swan-native-account-icon__badge-check {
        stroke: #4f0a1d !important;
        stroke-width: 1.7 !important;
      }

      .swan-native-account-icon__accent {
        stroke: #c9994c !important;
        stroke-width: 1.8 !important;
      }

      [data-swan-native-account-divider] {
        display: none !important;
      }

      @media (max-width: 520px) {
        [data-swan-native-account-shortcuts] {
          padding: 6px !important;
          border-radius: 16px !important;
        }

        [data-swan-native-account-link] {
          min-height: 76px !important;
          gap: 6px !important;
          padding: 8px 3px 7px !important;
          border-radius: 10px !important;
          font-size: 10.2px !important;
          letter-spacing: -.015em !important;
        }

        .swan-native-account-icon {
          width: 35px !important;
          height: 35px !important;
          min-width: 35px !important;
          flex-basis: 35px !important;
          border-radius: 10px !important;
        }

        .swan-native-account-icon svg {
          width: 21px !important;
          height: 21px !important;
        }
      }

    `;

    styleHost.appendChild(style);
  };

  const decorateLink = (link, key, layout = 'grid') => {
    link.setAttribute('data-swan-native-account-link', key);
    link.setAttribute('data-swan-native-account-layout', layout);
    link.classList.add(`swan-native-account-link--${key}`);

    if (!link.querySelector(':scope > .swan-native-account-icon')) {
      const icon = document.createElement('span');
      icon.className = `swan-native-account-icon swan-native-account-icon--${key}`;
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = ICONS[key];
      link.prepend(icon);
    }
  };

  const enhanceIndividualLinks = (scope, styleRoot) => {
    if (!rootBelongsToAccount(styleRoot) && Date.now() > accountInteractionUntil) return false;

    const matches = selectMatches(scope);
    let enhanced = false;

    Object.entries(matches).forEach(([key, link]) => {
      if (!link) return;
      injectStyles(styleRoot);
      decorateLink(link, key, 'row');
      enhanced = true;
    });

    return enhanced;
  };

  const enhanceScope = (scope, styleRoot) => {
    const matches = selectMatches(scope);
    const ordered = ['orders', 'profile', 'favourites'];
    if (!ordered.every((key) => matches[key])) return false;

    const links = ordered.map((key) => matches[key]);
    const container = commonAncestor(links, scope instanceof Element ? scope : null);
    if (!container || !container.classList) return false;

    const interactiveCount = interactiveElements(container).length;
    if (interactiveCount > 10) return false;

    injectStyles(styleRoot);
    container.setAttribute('data-swan-native-account-shortcuts', 'true');

    const targetBranches = new Set();

    ordered.forEach((key) => {
      const link = matches[key];
      const branch = directBranch(container, link);
      targetBranches.add(branch);

      branch.setAttribute('data-swan-native-account-branch', key);
      decorateLink(link, key, 'grid');
    });

    Array.from(container.children).forEach((child) => {
      if (targetBranches.has(child)) return;

      const role = normalize(child.getAttribute?.('role') || '');
      const className = normalize(typeof child.className === 'string' ? child.className : '');
      const tagName = normalize(child.tagName || '');
      const text = normalize(child.textContent);
      const hasInteractive = !!child.querySelector?.('a, button, [role="link"], [role="menuitem"]');
      const divider = tagName === 'hr'
        || role === 'separator'
        || /divider|separator/.test(className)
        || (!text && !hasInteractive);

      if (divider) child.setAttribute('data-swan-native-account-divider', 'true');
    });

    return true;
  };

  const scopesForRoot = (root) => {
    if (root === document) {
      return Array.from(document.querySelectorAll('dialog, [role="dialog"], [aria-modal="true"]'));
    }

    return [root];
  };

  const collectRoots = () => {
    const roots = new Set();
    roots.add(document);

    window.__swanCapturedShadowRoots?.forEach((root) => roots.add(root));

    let changed = true;
    while (changed) {
      changed = false;
      Array.from(roots).forEach((root) => {
        root.querySelectorAll?.('*').forEach((host) => {
          const nested = capturedRoot(host);
          if (nested && !roots.has(nested)) {
            roots.add(nested);
            changed = true;
          }
        });
      });
    }

    return Array.from(roots);
  };

  const observeRoot = (root) => {
    if (!root || ROOTS_OBSERVED.has(root)) return;
    ROOTS_OBSERVED.add(root);

    const target = root === document ? document.documentElement : root;
    if (!target) return;

    const observer = new MutationObserver(() => scheduleScan(20));
    observer.observe(target, { childList: true, subtree: true });
  };

  const bindAccount = (account) => {
    if (!(account instanceof Element) || ACCOUNTS_BOUND.has(account)) return;
    ACCOUNTS_BOUND.add(account);

    const rescan = () => {
      accountInteractionUntil = Date.now() + 5000;
      scheduleScan(0);
      [80, 180, 360, 700, 1200].forEach((delay) => window.setTimeout(() => scheduleScan(0), delay));
    };

    account.addEventListener('click', rescan, true);
    account.addEventListener('pointerup', rescan, true);
    account.addEventListener('open', rescan);
    account.addEventListener('toggle', rescan);
  };

  const scan = () => {
    collectRoots().forEach((root) => {
      observeRoot(root);
      scopesForRoot(root).forEach((scope) => {
        if (!enhanceScope(scope, root)) enhanceIndividualLinks(scope, root);
      });
      root.querySelectorAll?.('shopify-account').forEach(bindAccount);
    });

    document.querySelectorAll('shopify-account').forEach(bindAccount);
  };

  function scheduleScan(delay = 0) {
    window.clearTimeout(scanTimer);
    window.cancelAnimationFrame(scanFrame);

    scanTimer = window.setTimeout(() => {
      scanFrame = window.requestAnimationFrame(scan);
    }, delay);
  }

  const start = () => {
    scan();
    [100, 250, 500, 900, 1500, 2500, 4000].forEach((delay) => {
      window.setTimeout(() => scheduleScan(0), delay);
    });

    document.addEventListener('swan:shadow-root-created', (event) => {
      if (event.detail?.root) observeRoot(event.detail.root);
      scheduleScan(0);
    });

    document.addEventListener('shopify:section:load', () => scheduleScan(0));
    document.addEventListener('click', (event) => {
      if (event.target?.closest?.('shopify-account')) scheduleScan(30);
    }, true);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
